import React, { useCallback, useEffect, useRef } from 'react';
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';

import type { Props as LocationInputProps } from '@rover/components/src/formFields/LocationInput/LocationInput';
import LocationInput from '@rover/components/src/formFields/LocationInput/LocationInput';
import type { LatLng } from '@rover/types/src/LatLng';

import './LocationInput.types';

import { CURRENT_LOCATION_OPTION } from './LocationInput.constants';
import type { SelectOption } from './LocationInput.types';

export type Props = {
  enableLatLng?: boolean;
  inputHeight?: string;
  fetchGeocodedLatLng: (arg0: {
    currentLocationValue: string;
    guaranteePostalCode: boolean;
    handleGeocodeError: () => void;
    inputId: string;
    region?: string;
    selectedOption?: SelectOption;
    i18n: I18n;
  }) => void;
  geocodedLatLng?: LatLng<string>;
  geocodeResultsAreLoading?: boolean;
  guaranteePostalCode?: boolean;
  onClear?: () => void;
  onChange: (
    arg0: {
      label: string | null | undefined;
      lat?: string;
      lng?: string;
      locationAccuracy?: string;
      locationType?: string;
      rawLocationTypes?: string[];
      value: string | null | undefined;
    } | null
  ) => void;
  onGeocodeError?: () => void;
  region?: string;
  selectedOption?: SelectOption;
} & LocationInputProps; // this component takes care of the lat/lng logic

const LocationInputWrapper: React.FC<Props> = (props) => {
  const {
    enableLatLng,
    fetchGeocodedLatLng,
    geocodedLatLng,
    geocodeResultsAreLoading,
    guaranteePostalCode = false,
    onClear,
    onChange,
    onGeocodeError,
    region,
    selectedOption,
    inputHeight,
    inputFontSize,
    loading,
    disabled,
    ...other
  } = props;

  const { i18n } = useLingui();

  const prevGeocodedLatLng = useRef(geocodedLatLng);

  useEffect(() => {
    if (!enableLatLng) return;

    const prevLatLng = prevGeocodedLatLng.current;

    if (geocodedLatLng && !isEqual(prevLatLng, geocodedLatLng)) {
      onChange({ ...selectedOption, ...geocodedLatLng });
      prevGeocodedLatLng.current = geocodedLatLng;
    }
  }, [enableLatLng, geocodedLatLng, onChange, selectedOption]);

  const handleChange = useCallback(
    (newSelectedOption: any, currentLocationValue: string) => {
      if (enableLatLng) {
        fetchGeocodedLatLng({
          currentLocationValue,
          guaranteePostalCode,
          handleGeocodeError: () => {
            onGeocodeError?.();
          },
          inputId: props.inputId,
          region,
          selectedOption: newSelectedOption,
          i18n,
        });
      } else {
        onChange(newSelectedOption);
      }
    },
    [
      enableLatLng,
      fetchGeocodedLatLng,
      guaranteePostalCode,
      props.inputId,
      onChange,
      onGeocodeError,
      region,
      i18n,
    ]
  );

  return (
    <LocationInput
      {...omit(other, ['selectedOption'])}
      inputHeight={inputHeight}
      inputFontSize={inputFontSize}
      disabled={disabled || geocodeResultsAreLoading}
      loading={loading || geocodeResultsAreLoading}
      onClear={onClear}
      onChange={(option) => {
        const modifiedSelectedOption = option;
        handleChange(modifiedSelectedOption, CURRENT_LOCATION_OPTION(i18n).value);
      }}
    />
  );
}; // this component takes care of the lat/lng logic

export default LocationInputWrapper;
